import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { adminCourseKeys, courseKeys } from "@/lib/query";

export type AdminCourse = {
  id: string;
  slug: string;
  title: string;
  description: string;
  totalModules: number;
  isPublished: boolean;
  isFeatured: boolean;
  targetRoles: string[];
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
  previewUrl: string | null;
};

async function fetchAdminCourses(): Promise<AdminCourse[]> {
  const res = await fetch("/api/admin/courses");
  if (!res.ok) throw new Error("Failed to load courses");
  const json = (await res.json()) as { data: AdminCourse[] };
  return json.data;
}

export function useAdminCourses() {
  return useQuery({
    queryKey: adminCourseKeys.list(),
    queryFn: fetchAdminCourses,
  });
}

export function useToggleCoursePublished() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ courseId, isPublished }: { courseId: string; isPublished: boolean }) => {
      const res = await fetch(`/api/admin/courses/${courseId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ isPublished }),
      });
      if (!res.ok) throw new Error("Failed to update course");
    },
    onSuccess: (_data, { isPublished }) => {
      queryClient.invalidateQueries({ queryKey: adminCourseKeys.all });
      queryClient.invalidateQueries({ queryKey: courseKeys.all });
      toast.success(isPublished ? "Course published" : "Course unpublished");
    },
    onError: (err: Error) => {
      toast.error(err.message ?? "Failed to update course");
    },
  });
}

export function useSetCourseFeatured() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (courseId: string) => {
      const res = await fetch(`/api/admin/courses/${courseId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ isFeatured: true }),
      });
      if (!res.ok) throw new Error("Failed to set featured course");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminCourseKeys.all });
      queryClient.invalidateQueries({ queryKey: courseKeys.all });
      queryClient.invalidateQueries({ queryKey: ["featured-course"] });
      toast.success("Featured course updated");
    },
    onError: (err: Error) => {
      toast.error(err.message ?? "Failed to set featured course");
    },
  });
}

export function useUpdateCourseRoles() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ courseId, targetRoles }: { courseId: string; targetRoles: string[] }) => {
      const res = await fetch(`/api/admin/courses/${courseId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ targetRoles }),
      });
      if (!res.ok) throw new Error("Failed to update course roles");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminCourseKeys.all });
      toast.success("Course roles updated");
    },
    onError: (err: Error) => {
      toast.error(err.message ?? "Failed to update course roles");
    },
  });
}
